import { useEffect, useState, useRef, useCallback } from 'react';
import { getChatHistory } from '../../../../../../services/api';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const ChatHistory = ({ threadId, visible, videoId }) => {
  const [msgHistory, setMsgHistory] = useState([]);
  const [historyBody, setHistoryBody] = useState({});
  const [firstMsgId, setFirstMsgId] = useState('');
  const [lastMsgId, setLastMsgId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [fetchedThread, setFetchedThread] = useState(null);
  // const [currentVideoId, setCurrentVideoId] = useState(videoId);
  const containerRef = useRef(null);

  const fetchMessages = useCallback(
    async (initial = false) => {
      if (!threadId || isFetching) return;
      setIsFetching(true);

      try {
        const data = await getChatHistory(
          threadId,
          initial ? '' : lastMsgId, // after
          '', // before
          videoId,
        );
        console.log('Fetched chat history:', data);
        console.log('Current msgHistory length:', msgHistory.length);
        console.log('initial', initial);
        if (initial) {
          setMsgHistory(data.body.data);
        } else {
          setMsgHistory((prev) => [...prev, ...data.body.data]);
        }

        setHistoryBody(data);
        setFirstMsgId(data.body?.first_id || '');
        setLastMsgId(data.body?.last_id || '');
        setHasMore(data.has_more);
      } catch (error) {
        console.error('Error fetching chat history:', error);
      } finally {
        setIsFetching(false);
      }
    },
    [threadId, lastMsgId, isFetching, videoId],
  );

  // Auto-fetch when threadId or visibility changes
  useEffect(() => {
    console.log(
      'ChatHistory useEffect triggered with threadId:',
      threadId,
      'visible:',
      visible,
      'fetchedThread:',
      fetchedThread,
    );
    if (threadId && visible && fetchedThread !== threadId) {
      setFetchedThread(threadId);
      setMsgHistory([]);
      setLastMsgId('');
      fetchMessages(true);
    }
  }, [threadId, fetchedThread, visible, fetchMessages]);

  // Infinite scroll
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const handleScroll = () => {
      const threshold = 5; // px
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (distanceFromBottom <= threshold && hasMore && !isFetching) {
        fetchMessages(false);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, isFetching, fetchMessages]);

  // 🆕 Refresh button click handler
  const handleRefresh = () => {
    setMsgHistory([]);
    setLastMsgId('');
    setFirstMsgId('');
    setFetchedThread(null); // triggers useEffect again
    fetchMessages(true); // force reload
  };
  const { t } = useTranslation();
  return (
    <div
      className={visible ? 'd-flex flex-column h-80' : 'hidden'}
      style={{
        maxHeight: '800px',
      }}
    >
      <div className="flex justify-between">
        <h2 className="text-neutral-600 text-center text-2xl mb-3">
          {t('courses.lessons.detailsPage.chatHistory')}
        </h2>
        <div className="flex justify-end mb-3">
          <Button variant="outline" className="cursor-pointer" onClick={handleRefresh}>
            {t('courses.lessons.detailsPage.refreshHistory')}
          </Button>
        </div>
      </div>

      <div
        className="flex-grow-1 overflow-auto pe-2 h-75"
        ref={containerRef}
        // style={{
        //   maxHeight: '800px',
        // }}
      >
        {!msgHistory ||
          (!msgHistory.length && <p className="text-center">{t('global.loading')}</p>)}
        {msgHistory.filter(Boolean).length === 0 && (
          <p className="text-center"> {t('courses.lessons.detailsPage.openAiError')}</p>
        )}
        {msgHistory.length > 0 &&
          msgHistory.map((el) => {
            return (
              <div key={el.id} className="border-bottom pb-4 mb-4">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <p className="fw-bold">
                      {t('courses.lessons.detailsPage.detailedLessonConversationId', {
                        threadId,
                      })}
                    </p>
                    <p>
                      {new Date(el.created_at * 1000).toLocaleString('uk-UA', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                      })}
                    </p>
                  </div>

                  {el.tokenData && (
                    <div>
                      <p>
                        {t('courses.lessons.detailsPage.runTokens', {
                          tokens: el.tokenData.runUsage,
                        })}
                      </p>
                      <p>
                        {' '}
                        {t('courses.lessons.detailsPage.promptTokens', {
                          tokens: el.tokenData.promptTokens,
                        })}
                      </p>
                      <p>
                        {t('courses.lessons.detailsPage.completionTokens', {
                          tokens: el.tokenData.completionTokens,
                        })}
                      </p>
                    </div>
                  )}
                </div>

                {el.content.map((cont, i) => (
                  <div key={i}>
                    <p className="fw-bold">
                      {' '}
                      {t('courses.lessons.detailsPage.contentType', {
                        type: cont.type,
                      })}
                    </p>
                    {cont.type === 'text' ? (
                      <div style={{ whiteSpace: 'pre-line' }}>{cont.text.value}</div>
                    ) : (
                      <p>{cont.image_file?.file_id}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        {isFetching && <p className="text-center"> {t('global.loadingMore')}</p>}
      </div>
    </div>
  );
};

export default ChatHistory;
